(() => {
  const SCRIPT = document.querySelector('script[data-carry-hope-cart-guard]');
  if (!SCRIPT || window.__carryHopeCartGuardLoaded) return;
  window.__carryHopeCartGuardLoaded = true;

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const updateUrl = `${root}cart/update.js`;
  const addUrl = `${root}cart/add.js`;
  const fallbackPocketVariantId = Number(SCRIPT.dataset.pocketVariantId || 0);
  let repairing = null;

  const roleOf = (item) => item?.properties?.['_carry_hope_role'] || '';
  const groupOf = (item) => item?.properties?.['_carry_hope_group'] || '';
  const isPilotParent = (item) => roleOf(item) === 'finished-item' && item?.properties?.['_carry_hope_configuration'] === 'design-first-pilot-v1';
  const isCustomizationChild = (item) => ['pocket-customization','tote-second-side-customization'].includes(roleOf(item));
  const hasPocket = (item) => Boolean(item?.properties?.['_carry_hope_pocket_design'] || item?.properties?.['Left Chest Design'] || item?.properties?.['Pocket design']);
  const leftChestDesignOf = (item) => item?.properties?.['_carry_hope_pocket_design'] || item?.properties?.['Left Chest Design'] || item?.properties?.['Pocket design'] || '';

  async function request(url, payload) {
    const response = await fetch(url, {
      method: payload ? 'POST' : 'GET',
      headers: {'Accept': 'application/json', ...(payload ? {'Content-Type': 'application/json'} : {})},
      credentials: 'same-origin',
      body: payload ? JSON.stringify(payload) : undefined
    });
    const data = await response.json();
    if (!response.ok || data?.errors) throw new Error(data?.description || data?.message || data?.errors || 'Cart update failed');
    return data;
  }

  function expectedCustomizationVariant(parent) {
    return Number(parent?.properties?.['_carry_hope_required_child_variant_id'] || (hasPocket(parent) ? fallbackPocketVariantId : 0) || 0);
  }

  function requiredCustomizationRole(parent) {
    return parent?.properties?.['_carry_hope_required_child_role'] || 'pocket-customization';
  }

  function requiredCustomizationLabel(parent) {
    return parent?.properties?.['_carry_hope_required_child_customization'] || `Left Chest ${leftChestDesignOf(parent)}`;
  }

  async function repairCart() {
    if (repairing) return repairing;
    repairing = (async () => {
      const cart = await request(cartUrl);
      const parents = cart.items.filter(isPilotParent);
      const children = cart.items.filter(isCustomizationChild);
      const parentsByGroup = new Map(parents.map((item) => [groupOf(item), item]));
      const childrenByGroup = new Map();
      for (const child of children) {
        const group = groupOf(child);
        if (!childrenByGroup.has(group)) childrenByGroup.set(group, []);
        childrenByGroup.get(group).push(child);
      }

      const updates = {};
      const additions = [];
      let changed = false;

      for (const child of children) {
        const parent = parentsByGroup.get(groupOf(child));
        if (!parent || !expectedCustomizationVariant(parent)) {
          updates[child.key] = 0;
          changed = true;
        }
      }

      for (const parent of parents) {
        const group = groupOf(parent);
        const groupChildren = (childrenByGroup.get(group) || []).filter((child) => updates[child.key] !== 0);
        if (!expectedCustomizationVariant(parent)) {
          for (const child of groupChildren) {
            updates[child.key] = 0;
            changed = true;
          }
          continue;
        }

        const expectedVariant = expectedCustomizationVariant(parent);
        const expectedRole = requiredCustomizationRole(parent);
        const validChildren = groupChildren.filter((child) => Number(child.variant_id) === expectedVariant && roleOf(child) === expectedRole);
        const keeper = validChildren[0];
        for (const child of groupChildren) {
          if (child !== keeper) {
            updates[child.key] = 0;
            changed = true;
          }
        }

        const hasParentRelationship = Boolean(keeper?.parent_relationship?.parent_key || keeper?.parent_relationship?.parent?.key);
        if (!keeper || !hasParentRelationship) {
          if (!expectedVariant) throw new Error('Required customization variant is unavailable');
          updates[parent.key] = 0;
          for (const child of groupChildren) updates[child.key] = 0;
          additions.push(
            {
              id: parent.variant_id,
              quantity: parent.quantity,
              properties: parent.properties
            },
            {
              id: expectedVariant,
              quantity: parent.quantity,
              parent_id: parent.variant_id,
              properties: {
                'Customization': requiredCustomizationLabel(parent),
                '_carry_hope_group': group,
                '_carry_hope_role': expectedRole
              }
            }
          );
          changed = true;
        } else if (keeper.quantity !== parent.quantity) {
          updates[keeper.key] = parent.quantity;
          changed = true;
        }
      }

      if (!changed) return {changed: false, cart};
      if (Object.keys(updates).length) await request(updateUrl, {updates});
      if (additions.length) await request(addUrl, {items: additions});
      return {changed: true};
    })().finally(() => { repairing = null; });
    return repairing;
  }

  async function verifyAndRefresh() {
    try {
      const result = await repairCart();
      if (!result.changed) return;
      const now = Date.now();
      const state = JSON.parse(sessionStorage.getItem('carry-hope-cart-repair') || '{}');
      const attempts = now - Number(state.timestamp || 0) < 15000 ? Number(state.attempts || 0) + 1 : 1;
      sessionStorage.setItem('carry-hope-cart-repair', JSON.stringify({attempts, timestamp: now}));
      if (attempts <= 3) window.location.reload();
    } catch (error) {
      console.error('[Carry Hope cart]', error);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ch-cart-side]');
    if (!button) return;
    const mockup = button.closest('[data-ch-cart-mockup]');
    if (!mockup) return;
    const side = button.dataset.chCartSide;
    mockup.querySelectorAll('[data-ch-cart-side]').forEach((control) => control.setAttribute('aria-pressed', String(control === button)));
    mockup.querySelectorAll('[data-ch-cart-panel]').forEach((panel) => { panel.hidden = panel.dataset.chCartPanel !== side; });
  });

  document.addEventListener('DOMContentLoaded', verifyAndRefresh, {once: true});
  window.addEventListener('pageshow', verifyAndRefresh);
})();
